// Matching transactions inside one company: the decisions, as pure functions.
//
// The engine already proposes — `_match_suggestions` runs per party and offers
// 1:1 pairs, bounded combinations and an aggregate, each with its evidence
// band. This is the other half: what a person does when the machine has
// nothing to offer, or when its offer is not the pairing they can see.
//
// `manual_match` has allowed n:m within one party all along. What was missing
// was a surface: the company panel said "match by hand on the sheet", which
// sent the reconciler to a flat 57,000-row list to do work that belongs beside
// the company it concerns.

/** One unmatched entry, from either side. Only the three fields these rules
 *  need — the row renderer takes the whole `DifferenceRow`. */
export interface MatchEntry {
  fingerprint: string
  /** the entry's own TDS, as the string the API sends */
  tds: string
  /** the entry's own date (26AS date for a 26AS row, books date for a books
   *  row), ISO or null */
  date: string | null
}

/** Above this many entries in a company, a period must be chosen first.
 *
 *  Measured rather than guessed: on run 34 the median both-sided company is 37
 *  entries and lists comfortably, while CGHS is 1,841 x 29,548. A column of
 *  29,548 rows is not a picker, and paging through it would not make it one —
 *  the reconciler needs a period, which is the natural unit of transaction
 *  matching anyway.
 */
export const MATCH_LIST_CAP = 200

export function matchWindow(n26: number, nBooks: number) {
  const total = n26 + nBooks
  return { total, needsPeriod: total > MATCH_LIST_CAP }
}

/** Money as integer paise.
 *
 *  Not `Number(a) + Number(b)`. The residual decides whether the reconciler is
 *  asked to justify an untied match, and a tenth of a rupee invented by binary
 *  floating point would demand a reason for a group that actually ties — or,
 *  worse, hide one that does not. Paise are exact at every magnitude this
 *  screen sees.
 */
function paise(v: string | null | undefined): number {
  if (v == null || v === '') return 0
  const neg = String(v).trim().startsWith('-')
  const [whole, frac = ''] = String(v).trim().replace(/^[-+]/, '').split('.')
  const p = Number(whole || 0) * 100 + Number((frac + '00').slice(0, 2))
  return neg ? -p : p
}

const rupees = (p: number) =>
  `${p < 0 ? '-' : ''}${Math.floor(Math.abs(p) / 100)}.${String(Math.abs(p) % 100).padStart(2, '0')}`

export interface TieState {
  sum_26as: string
  sum_books: string
  /** 26AS minus books, signed — which side is short is the whole question */
  residual: string
  /** the gap exceeds the run's tolerance, so the server will require a reason
   *  and this screen asks for it while you are still deciding */
  needsReason: boolean
  ready: boolean
}

/** What the selection currently adds up to, and whether it needs explaining.
 *
 *  Matching a group that does not tie is legitimate — a short deduction, a
 *  rounding, a part payment against the same invoices. Doing it silently is
 *  not: the residual would leave the party reporting RECONCILED while carrying
 *  a real difference. The tolerance is the run's own materiality, read from the
 *  run rather than decided here.
 */
export function tieState(
  picked26: MatchEntry[],
  pickedBooks: MatchEntry[],
  tolerance: string,
): TieState {
  const s26 = picked26.reduce((a, x) => a + paise(x.tds), 0)
  const sbk = pickedBooks.reduce((a, x) => a + paise(x.tds), 0)
  const residual = s26 - sbk
  const ready = picked26.length > 0 && pickedBooks.length > 0
  return {
    sum_26as: rupees(s26),
    sum_books: rupees(sbk),
    residual: rupees(residual),
    // an empty selection is not an untied match
    needsReason: ready && Math.abs(residual) > paise(tolerance),
    ready,
  }
}

/** The request body's `fingerprints`: ONE flat list of both sides, which is
 *  what the endpoint takes — it reads each row's status to tell the sides
 *  apart rather than trusting the caller to. Deduplicated, because a repeat
 *  makes the server refuse the whole group for what is only a UI slip. */
export function matchSelection(
  picked26: MatchEntry[],
  pickedBooks: MatchEntry[],
): string[] {
  return [...new Set([...picked26, ...pickedBooks].map((e) => e.fingerprint))]
}

/** Which counterparts fall within `days` of the picked entry.
 *
 *  Advisory only — nothing is hidden or disabled. A genuine match can sit
 *  outside any window (a March deduction booked in the next financial year is
 *  the commonest residual on this engagement), so this de-emphasises rather
 *  than filters. It answers "which of these is the same money" the way the
 *  name columns re-rank when you pick a name.
 *
 *  An undated entry is absent from the result: that is "cannot be judged", not
 *  "is far away", and the UI should render it plainly rather than dimmed.
 */
export function nearInWindow(
  pickedDate: string | null,
  others: MatchEntry[],
  days: number,
): Set<string> {
  const out = new Set<string>()
  if (!pickedDate) return out
  const at = Date.parse(pickedDate)
  if (Number.isNaN(at)) return out
  const span = days * 86400000
  for (const o of others) {
    if (!o.date) continue
    const t = Date.parse(o.date)
    if (!Number.isNaN(t) && Math.abs(t - at) <= span) out.add(o.fingerprint)
  }
  return out
}

/** May this selection be submitted?
 *
 *  The server's own rule, stated once here so the button and the endpoint
 *  cannot disagree: both sides non-empty, and — where the residual exceeds
 *  tolerance — a reason that survives stripping. Enabling the button on a
 *  whitespace reason only moves the refusal to after the click.
 */
export function canSubmit(tie: TieState, reason: string): boolean {
  if (!tie.ready) return false
  return !tie.needsReason || reason.trim().length > 0
}

/** The kinds `_match_suggestions` emits, which are NOT all graded.
 *
 *  `band_of` never returns null — it always yields strong / medium / weak from
 *  an offer's own facts. A null band therefore means no evidence dict was
 *  recorded at all, and that happens for exactly two kinds: pass-1 pairs
 *  (`within tolerance`) and the leftover `aggregate`. Neither is weak.
 *
 *  The old rule was `band ?? 'weak'`, which filed every one of ADITYA BIRLA's
 *  293 same-amount pairs under Weak — a band whose stated rule is "4 entries,
 *  or entries with no date to corroborate them". A 1:1 amount match is the
 *  plainest thing on the screen, and it was being shown as the least reliable
 *  under a sentence that is not true of it.
 */
const SAME_AMOUNT = 'within tolerance'
const AGGREGATE = 'aggregate'

export interface OfferGroup<T> {
  key: 'same_amount' | 'strong' | 'medium' | 'weak' | 'aggregate'
  label: string
  /** the statement of fact the reconciler judges once and applies to the band */
  rule: string
  color: string
  offers: T[]
}

const GROUPS: { key: OfferGroup<unknown>['key']; label: string; rule: string; color: string }[] = [
  // "Same amount" was not true of these rows and the screen showed it: 4,855.50
  // against 4,856.00, 44,537.80 against 44,537.00. Every pair is inside the
  // run's tolerance, which is the actual rule, and on a screen whose whole job
  // is the reconciler's judgement a label they can see is wrong costs more than
  // the two extra words.
  { key: 'same_amount', label: 'Ties within tolerance', color: 'teal',
    rule: "one 26AS entry and one books entry agreeing to within the run's "
      + 'tolerance, paired in date order' },
  { key: 'strong', label: 'Strong', color: 'green',
    rule: '2 entries, within 3 days of the receipt, and nothing else in this company sums to it' },
  { key: 'medium', label: 'Medium', color: 'blue',
    rule: '2 or 3 entries, inside the date window, and nothing else in this company sums to it' },
  { key: 'weak', label: 'Weak', color: 'orange',
    rule: '4 entries, or entries with no date to corroborate them — worth reading one at a time' },
  { key: 'aggregate', label: 'Everything else, which nets', color: 'gray',
    rule: 'all this company\'s remaining entries at once — they add up, but nothing says they pair' },
]

export function offerGroups<T extends { kind?: string | null; band?: string | null }>(
  offers: T[],
): OfferGroup<T>[] {
  const keyOf = (o: T): OfferGroup<T>['key'] => {
    if (o.kind === SAME_AMOUNT) return 'same_amount'
    if (o.kind === AGGREGATE) return 'aggregate'
    // only the combo kinds are graded, so an ungraded combo is genuinely weak
    // — that is `band_of`'s own floor, not an invention here
    return (o.band as OfferGroup<T>['key']) ?? 'weak'
  }
  return GROUPS
    .map((g) => ({ ...g, offers: offers.filter((o) => keyOf(o) === g.key) }))
    .filter((g) => g.offers.length > 0)
}

// ---- which period to work in ----------------------------------------------
//
// The gate used to offer MONTHS, which is the wrong unit twice over. TDS is
// filed quarterly (24Q/26Q) and 26AS is organised that way, so a month splits
// one quarter's deductions across two lists. And a bare list of periods says
// nothing about where the work is, so finding it meant opening four of them.
//
// The counts come from the server, computed through the same filter the columns
// are fetched with, so an option cannot advertise a number the list then fails
// to show.

export interface MatchPeriod {
  key: string
  label: string
  from: string
  to: string
  n_26as: number
  n_books: number
}

export interface PeriodChoice {
  value: string
  label: string
  /** still more entries than the cap — offered, with a warning, never blocked */
  heavy: boolean
  empty: boolean
}

/** The picker's options, each carrying what it holds.
 *
 *  Empty periods stay on the list and stay selectable. They are a fact about
 *  the year worth seeing — a quarter with nothing open is an answer — and
 *  hiding them would make the year's shape unreadable.
 *
 *  Nothing is disabled. `FY` on a 276 x 319 company is exactly the list the cap
 *  exists to prevent, and it is still offered: the cap is advice about size,
 *  not a rule about what the reconciler may look at.
 */
export function periodChoices(
  periods: MatchPeriod[],
  cap: number = MATCH_LIST_CAP,
): PeriodChoice[] {
  return periods.map((p) => {
    const total = p.n_26as + p.n_books
    return {
      value: p.key,
      label: total === 0
        ? `${p.label} — nothing open`
        : `${p.label} — ${p.n_26as} × ${p.n_books}`,
      heavy: total > cap,
      empty: total === 0,
    }
  })
}

/** The warning under a chosen period, or null when it is a comfortable size. */
export function periodWarning(
  periods: MatchPeriod[],
  key: string | null,
  cap: number = MATCH_LIST_CAP,
): string | null {
  const p = periods.find((x) => x.key === key)
  if (!p) return null
  const total = p.n_26as + p.n_books
  if (total <= cap) return null
  return `${p.label} is ${p.n_26as} × ${p.n_books} entries — a long list to work `
    + 'through. A quarter is usually easier.'
}

/** The most fingerprints one offer may carry on the wire — `AcceptOffersIn`'s
 *  own bound, stated here so the screen and the schema cannot disagree. */
export const OFFER_FP_MAX = 64

/** Split a band into what can go on the wire and what cannot.
 *
 *  Found live on NEW INDIA: the leftover `aggregate` offer carries EVERY
 *  remaining open entry on both sides — hundreds of fingerprints against the
 *  schema's 64 — and "Accept all" sent it anyway. FastAPI rejects the whole
 *  request before the handler runs, so the one impossible offer silently took
 *  every possible offer down with it, and the reconciler got a raw validation
 *  error for an action the screen itself proposed.
 *
 *  This is a statement about the wire, not a judgement about the offer: a
 *  blocked offer is still real work, reachable through Match by hand a period
 *  at a time. The screen's job is to say that instead of failing.
 */
export function sendable<T extends { fingerprints: string[] }>(
  offers: T[],
): { ok: T[]; blocked: T[] } {
  const ok: T[] = []
  const blocked: T[] = []
  for (const o of offers) {
    const n = o.fingerprints.length
    ;(n >= 2 && n <= OFFER_FP_MAX ? ok : blocked).push(o)
  }
  return { ok, blocked }
}

/** The most offers one accept-request may carry — `AcceptOffersIn`'s own
 *  bound. The server's cap is a DoS guard and stays; the screen sends chunks. */
export const ACCEPT_BATCH_MAX = 500

/** Split a batch into wire-sized requests, order preserved.
 *
 *  Found live: "Accept all 823" on NEW INDIA sent one request and the server
 *  refused it whole — 823 acceptable offers rejected for arriving together.
 */
export function chunked<T>(items: T[], max: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += max) out.push(items.slice(i, i + max))
  return out
}

/** Offer members that stand for several physically identical entries.
 *
 *  A fingerprint is content-derived, so N identical entries share one — and a
 *  claim on it claims all N. The offer pool folds such a group into ONE item
 *  worth the sum of its copies, which makes the whole group matchable (134 ×
 *  Rs 56 against one Rs 7,504 books line) while a SUBSET stays impossible.
 *
 *  The server refuses to take copies the caller has not accounted for, so the
 *  count travels with the request rather than being inferred there.
 */
interface CopyMember { fingerprint?: string | null; copies?: number }
interface CopyOffer {
  fingerprints: string[]
  members_26as: CopyMember[]
  members_books: CopyMember[]
}

export function copiesOf(offer: CopyOffer): Record<string, number> {
  const out: Record<string, number> = {}
  for (const m of [...offer.members_26as, ...offer.members_books]) {
    if (m.fingerprint && (m.copies ?? 1) > 1) out[m.fingerprint] = m.copies as number
  }
  return out
}

/** The gate sentence's entry counts — one arithmetic per screen.
 *
 *  The period picker counts OPEN entries; the gate sentence counted the offers
 *  payload's totals, and the two disagreed on one screen (332 × 338 over
 *  lists of 310 × 328). When the open counts are in hand and differ, both are
 *  shown with labels; while they load, the totals stand alone.
 */
export function openCountLabel(
  n26: number,
  nBooks: number,
  fy?: { n_26as: number; n_books: number } | null,
): string {
  const g = (n: number) => n.toLocaleString('en-IN')
  const total = `${g(n26)} × ${g(nBooks)} entries`
  if (fy && (fy.n_26as !== n26 || fy.n_books !== nBooks)) {
    return `${total}, ${g(fy.n_26as)} × ${g(fy.n_books)} still open`
  }
  return total
}

/** The empty-offers headline, honest about what the run already took.
 *
 *  "Nothing the engine can line up here" read as "the engine did nothing"
 *  when the run had quietly matched pairs at tolerance and merely had nothing
 *  MORE to offer (fleet #8).
 */
export function nothingToOfferText(toleranceMatched: number | undefined): string {
  const n = toleranceMatched ?? 0
  if (n > 0) {
    return `The run already matched ${n} pair${n === 1 ? '' : 's'} within `
      + 'tolerance — nothing more to offer. Match by hand below, or explain '
      + 'what is left.'
  }
  return 'Nothing the engine can line up here — match by hand below, or '
    + 'explain what is left.'
}

/** The payload for taking one option of a contested receipt.
 *
 *  The contested block names each ambiguous receipt with BOTH candidate sets
 *  and rightly refuses to choose. This is the human's choice, routed through
 *  the same manual-match door every hand-built group goes through: the
 *  receipt's fingerprint plus the chosen set's, with copies stated for any
 *  folded member — the server must never infer a sweep of identical copies
 *  nobody chose. Null for an option that no longer exists (a stale click).
 */
interface ContestedMemberLike { fingerprint: string; copies?: number }

export function contestedSelection(
  receipt: { target: ContestedMemberLike; options: ContestedMemberLike[][] },
  optionIdx: number,
): { fingerprints: string[]; expect_copies?: Record<string, number> } | null {
  const opt = receipt.options[optionIdx]
  if (!opt || opt.length === 0) return null
  const members = [receipt.target, ...opt]
  const fingerprints = [...new Set(members.map((m) => m.fingerprint))]
  const copies: Record<string, number> = {}
  for (const m of members) {
    if ((m.copies ?? 1) > 1) copies[m.fingerprint] = m.copies as number
  }
  return {
    fingerprints,
    ...(Object.keys(copies).length ? { expect_copies: copies } : {}),
  }
}

/** How many ENTRIES accepting this offer disposes of.
 *
 *  Not the number of fingerprints: a grouped member is one line on screen and
 *  several entries in the reconciliation, and a screen that counts lines would
 *  understate what one click does — on run 41, by up to 133.
 */
export function entriesIn(offer: CopyOffer): number {
  return [...offer.members_26as, ...offer.members_books]
    .reduce((n, m) => n + (m.copies ?? 1), 0)
}

/** Item 13 — rank-on-pick: the other column re-orders by how CLOSE each entry
 *  is to the picked one, date distance first (a booking usually lags its
 *  deduction by days, not amounts), amount distance second. Undated rows sink
 *  when the pick has a date — closeness cannot be judged, and a guess ranked
 *  high would read as the machine's opinion. Nothing is hidden: the same rule
 *  as the alias picker's "Ranked against X — everything is still listed."
 */
export function closenessOrder<T extends { date: string | null; amount: string }>(
  rows: T[],
  picked: { date: string | null; amount: string } | null,
): T[] {
  if (!picked) return rows
  const day = 24 * 60 * 60 * 1000
  const dateDist = (d: string | null): number | null => {
    if (!picked.date || !d) return null
    return Math.abs(+new Date(d) - +new Date(picked.date)) / day
  }
  const amtDist = (a: string) =>
    Math.abs(Number(a) - Number(picked.amount))
  return [...rows].sort((a, b) => {
    const da = dateDist(a.date)
    const db = dateDist(b.date)
    if (da !== db) {
      if (da === null) return 1
      if (db === null) return -1
      if (da !== db) return da - db
    }
    return amtDist(a.amount) - amtDist(b.amount)
  })
}
